import React, { useState, useEffect } from 'react'
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, CircularProgress, IconButton, Chip, Grid, Select, MenuItem, FormControl } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import StarIcon from '@mui/icons-material/Star'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { axiosi } from '../config/axios'
import { selectLoggedInUser } from '../features/auth/AuthSlice'

const ORDER_STATUSES = ['Pending', 'Dispatched', 'Out for delivery', 'Cancelled']

const getCustomerLabel = (order) => {
  const user = order?.user
  if (!user) return '—'
  if (typeof user === 'object') {
    if (user.name) return user.email ? `${user.name} (${user.email})` : user.name
    return user.email || '—'
  }
  return '—'
}

const getOrderStatusColor = (status) => {
  if (status === 'Pending') return 'warning'
  if (status === 'Dispatched') return 'info'
  if (status === 'Out for delivery') return 'primary'
  if (status === 'Cancelled') return 'error'
  return 'default'
}

const getDeliveryAddress = (order) => Array.isArray(order?.address) ? order.address[0] : order?.address

const getDeliveryCustomerLabel = (order) => {
  const address = getDeliveryAddress(order)
  return address?.name || getCustomerLabel(order)
}

const getOrderItemsLabel = (order) => {
  const items = Array.isArray(order?.item) ? order.item : Array.isArray(order?.items) ? order.items : []
  if (!items.length) return 'No products'
  return items.map((lineItem) => {
    const title = lineItem?.product?.title || lineItem?.title || 'Unknown product'
    return `${title} x ${lineItem?.quantity || 1}`
  }).join(', ')
}

const getProductStockQuantity = (product) => Number(product?.stockQuantity ?? product?.stock ?? 0)

const AdminPanelTabs = ({ activeTab }) => {
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [paymentProof, setPaymentProof] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({})
  const [items, setItems] = useState([])
  const loggedInUser = useSelector(selectLoggedInUser)

  // Handle quick toggle for product flags
  const handleToggleFlag = async (productId, flagName) => {
    try {
      const product = items.find(p => p._id === productId)
      if (!product) return
      
      const updateData = { [flagName]: !product[flagName] }
      await axiosi.patch(`/products/${productId}`, updateData, {
        headers: { Authorization: `Bearer ${loggedInUser?.token}` }
      })
      
      // Update local state
      setItems(items.map(item => 
        item._id === productId 
          ? { ...item, [flagName]: !item[flagName] }
          : item
      ))
    } catch (error) {
      console.error('Toggle flag error:', error)
    }
  }

  // Tab 0: Products
  const renderProducts = () => (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          component={Link}
          to="/admin/add-product"
        >
          Add New Product
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          component={Link}
          to="/admin/add-brand"
        >
          Add Brand
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          component={Link}
          to="/admin/add-category"
        >
          Add Category
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Product Name</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Price</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Stock</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Recommended</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Top Seller</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((product) => (
              <TableRow key={product._id} hover>
                <TableCell>{product.title}</TableCell>
                <TableCell align="right">₨{product.price}</TableCell>
                <TableCell align="right">
                  <Chip 
                    label={getProductStockQuantity(product) > 0 ? `${getProductStockQuantity(product)} units` : 'Out of Stock'}
                    color={getProductStockQuantity(product) > 0 ? 'success' : 'error'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    size="small"
                    color={product.isRecommended ? 'warning' : 'default'}
                    onClick={() => handleToggleFlag(product._id, 'isRecommended')}
                  >
                    <StarIcon />
                  </IconButton>
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    size="small"
                    color={product.isTopSeller ? 'error' : 'default'}
                    onClick={() => handleToggleFlag(product._id, 'isTopSeller')}
                  >
                    <TrendingUpIcon />
                  </IconButton>
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small"
                    href={`/admin/product-update/${product._id}`}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small"
                    color="error"
                    onClick={() => handleDelete(product._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )

  // Tab 1: Discounts
  const renderDiscounts = () => (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => { setEditingId(null); setFormData({}); setDialogOpen(true); }}
        >
          Create Discount Code
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Code</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Discount %</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((coupon) => (
              <TableRow key={coupon._id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{coupon.code}</TableCell>
                <TableCell align="right">{coupon.discountPercentage}%</TableCell>
                <TableCell align="right">
                  <Chip 
                    label={coupon.isActive ? 'Active' : 'Inactive'}
                    color={coupon.isActive ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small"
                    color="primary"
                    onClick={() => { setEditingId(coupon._id); setFormData(coupon); setDialogOpen(true); }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small"
                    color="error"
                    onClick={() => handleDelete(coupon._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )

  // Tab 2: Testers
  const renderTesters = () => (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          component={Link}
          to="/admin/add-tester"
        >
          Add New Tester
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tester Name</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Brand</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Price</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Stock</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((tester) => (
              <TableRow key={tester._id} hover>
                <TableCell>{tester.name}</TableCell>
                <TableCell align="right">{tester.brand}</TableCell>
                <TableCell align="right">₨{tester.price}</TableCell>
                <TableCell align="right">
                  <Chip 
                    label={tester.inStock ? 'In Stock' : 'Out of Stock'}
                    color={tester.inStock ? 'success' : 'error'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => handleDelete(tester._id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )

  // Tab 3: Reviews
  const renderReviews = () => (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Product</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Rating</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((review) => (
              <TableRow key={review._id} hover>
                <TableCell>{review.user?.name || 'Unknown'}</TableCell>
                <TableCell align="right">{review.product?.title || 'Unknown'}</TableCell>
                <TableCell align="right">⭐ {review.rating}</TableCell>
                <TableCell align="right">
                  <Chip 
                    label={review.approved ? 'Approved' : 'Pending'}
                    color={review.approved ? 'success' : 'warning'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small"
                    color="success"
                    onClick={() => handleApproveReview(review._id)}
                  >
                    ✓
                  </IconButton>
                  <IconButton 
                    size="small"
                    color="error"
                    onClick={() => handleDelete(review._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )

  // Tab 4: Orders
  const renderOrders = () => (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Order ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Items</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Address</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Payment</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>TRX / Proof</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Update</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">No orders yet</TableCell>
              </TableRow>
            )}
            {items.map((order) => {
              const address = getDeliveryAddress(order)
              return (
              <TableRow key={order._id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }} title={order._id}>
                  {order._id?.slice(-8)}
                </TableCell>
                <TableCell>{getDeliveryCustomerLabel(order)}</TableCell>
                <TableCell align="right" sx={{ maxWidth: 260 }}>{getOrderItemsLabel(order)}</TableCell>
                <TableCell align="right">
                  <Stack alignItems="flex-end">
                    <Box>{address?.street || 'No street'}</Box>
                    <Box>{[address?.city, address?.state, address?.country, address?.postalCode].filter(Boolean).join(', ') || 'No city/state'}</Box>
                    <Box>{address?.phoneNumber || 'No phone'}</Box>
                  </Stack>
                </TableCell>
                <TableCell align="right">{order.paymentMode || '—'}</TableCell>
                <TableCell align="right">
                  {order.paymentMode === 'ONLINE' ? (
                    <Stack alignItems="flex-end" rowGap={1}>
                      <Box>{order.trxId || 'No TRX ID'}</Box>
                      {order.paymentProof ? (
                        <Button size="small" variant="outlined" onClick={() => setPaymentProof(order.paymentProof)}>
                          View Screenshot
                        </Button>
                      ) : (
                        <Box>No screenshot</Box>
                      )}
                    </Stack>
                  ) : (
                    <Box>COD</Box>
                  )}
                </TableCell>
                <TableCell align="right">
                  Rs. {Number(order.total ?? 0).toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell align="right">
                  <Chip 
                    label={order.status || 'Pending'}
                    color={getOrderStatusColor(order.status)}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={order.status || 'Pending'}
                      onChange={(e) => handleUpdateOrderStatus(order._id, e.target.value)}
                    >
                      {ORDER_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )

  // Tab 5: Settings
  const renderSettings = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Button 
          variant="contained" 
          color="primary"
          href="/admin/payment-settings"
          fullWidth
        >
          💳 Update Payment Settings & Bank Details
        </Button>
      </Grid>
    </Grid>
  )

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        setLoading(true)
        const endpoint = getEndpoint()
        await axiosi.delete(`/${getEndpoint()}/${id}`, {
          headers: { Authorization: `Bearer ${loggedInUser?.token}` }
        })
        if (endpoint === 'products') {
          setItems((prev) => prev.filter((item) => item._id !== id))
        } else {
          fetchItems()
        }
      } catch (error) {
        console.error('Delete error:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleApproveReview = async (reviewId) => {
    try {
      setLoading(true)
      await axiosi.patch(`/reviews/${reviewId}`, { approved: true })
      fetchItems()
    } catch (error) {
      console.error('Approve error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      setLoading(true)
      await axiosi.patch(`/orders/${orderId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${loggedInUser?.token}` }
      })
      setItems((prev) =>
        prev.map((order) =>
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      )
    } catch (error) {
      console.error('Update error:', error)
      fetchItems()
    } finally {
      setLoading(false)
    }
  }

  const getEndpoint = () => {
    const endpoints = ['products', 'coupons', 'testers', 'reviews', 'orders']
    return endpoints[activeTab]
  }

  const fetchItems = async () => {
    try {
      setLoading(true)
      const endpoint = getEndpoint()
      const response = await axiosi.get(`/${endpoint}`)
      const data = response.data.data || response.data
      setItems(endpoint === 'products' ? data.filter((item) => !item.isDeleted) : data)
    } catch (error) {
      console.error('Fetch error:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const renderContent = () => {
    if (loading && items.length === 0) return <CircularProgress sx={{ m: 'auto', display: 'block' }} />
    
    switch (activeTab) {
      case 0: return renderProducts()
      case 1: return renderDiscounts()
      case 2: return renderTesters()
      case 3: return renderReviews()
      case 4: return renderOrders()
      case 5: return renderSettings()
      default: return null
    }
  }

  return (
    <>
      {renderContent()}
      
      {/* Discount Dialog */}
      <Dialog open={dialogOpen && activeTab === 1} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Discount Code' : 'Create New Discount Code'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Coupon Code"
            value={formData.code || ''}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            margin="normal"
            placeholder="SAVE20"
          />
          <TextField
            fullWidth
            label="Discount Percentage"
            type="number"
            value={formData.discountPercentage || ''}
            onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              if (editingId) {
                await axiosi.patch(`/coupons/${editingId}`, formData)
              } else {
                await axiosi.post('/coupons', formData)
              }
              fetchItems()
              setDialogOpen(false)
            } catch (error) {
              console.error('Save error:', error)
            }
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(paymentProof)} onClose={() => setPaymentProof(null)} maxWidth="md" fullWidth>
        <DialogTitle>Payment Screenshot</DialogTitle>
        <DialogContent>
          {paymentProof && (
            <Box
              component="img"
              src={paymentProof}
              alt="Payment proof"
              sx={{ width: '100%', maxHeight: '75vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentProof(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AdminPanelTabs
